import { format } from "date-fns";
import { type FC, useEffect, useRef, useState } from "react";
import type { Platform } from "shared";
import { Icon } from "../common/Icon";
import { useIsTablet } from "../hooks/useBreakpoint";
import { LinkGen } from "../LinkGen";
import { InboxAvatar } from "./InboxAvatar";
import { InboxMessage } from "./InboxMessage";
import { InboxReplyComposer } from "./InboxReplyComposer";
import { useInboxActions, useInboxConversation } from "./useInbox";
import "./InboxConversationView.css";

interface InboxConversationViewProps {
	conversationId: number;
	onClose: () => void;
}

const platformLabels: Partial<Record<Platform, string>> = {
	facebook: "Facebook",
	instagram: "Instagram",
	linkedin: "LinkedIn",
	threads: "Threads",
	tiktok: "TikTok",
	wordpress: "WordPress",
	"custom-blog": "Blog",
};

export const InboxConversationView: FC<InboxConversationViewProps> = ({
	conversationId,
	onClose,
}) => {
	const isTablet = useIsTablet();
	const { data } = useInboxConversation(conversationId);
	const {
		markAsRead,
		sendMessage,
		archiveConversation,
		unarchiveConversation,
		setCommentHidden,
		suggestReply,
	} = useInboxActions();
	const messagesEndRef = useRef<HTMLDivElement>(null);
	const [draft, setDraft] = useState("");
	const [replyTargetId, setReplyTargetId] = useState<number | undefined>();

	const { conversation, messages } = data;
	const markAsReadMutate = markAsRead.mutate;

	// Mark as read when viewing. Invalidate the open conversation so unreadCount
	// clears and this effect does not keep re-firing.
	useEffect(() => {
		if (conversation.unreadCount > 0) {
			markAsReadMutate(conversationId);
		}
	}, [conversationId, conversation.unreadCount, markAsReadMutate]);

	// Scroll to bottom when messages change
	useEffect(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
	}, []);

	const handleSendMessage = async (content: string) => {
		await sendMessage.mutateAsync({
			conversationId,
			content,
			replyToMessageId: replyTargetId,
		});
		setReplyTargetId(undefined);
	};

	const handleSuggestReply = async (messageId: number) => {
		const reply = await suggestReply.mutateAsync({
			conversationId,
			messageId,
		});
		setDraft(reply);
		setReplyTargetId(messageId);
	};

	// TikTok is the only platform exposing comment hide/unhide today.
	const canModerate =
		conversation.conversationType === "comment" &&
		conversation.platform === "tiktok";

	const handleArchiveToggle = () => {
		if (conversation.isArchived) {
			unarchiveConversation.mutate(conversationId);
		} else {
			archiveConversation.mutate(conversationId);
		}
	};

	const headerAvatarUrl =
		conversation.conversationType === "comment"
			? conversation.postThumbnailUrl || conversation.participantAvatarUrl
			: conversation.participantAvatarUrl;

	return (
		<div className="inbox-view">
			<div className="inbox-view-header">
				<div className="inbox-view-header-info">
					<InboxAvatar
						url={headerAvatarUrl}
						label={
							conversation.participantName ||
							conversation.participantUsername ||
							"Post"
						}
						variant={
							conversation.conversationType === "comment" ? "post" : "person"
						}
					/>
					<div className="inbox-view-details">
						<h5 className="inbox-view-name">
							{conversation.participantName ||
								conversation.participantUsername ||
								"Unknown"}
						</h5>
						<span className="inbox-view-meta">
							via{" "}
							{platformLabels[conversation.platform as Platform] ||
								conversation.platform}{" "}
							{conversation.conversationType === "comment"
								? "Comment"
								: "Message"}
							{conversation.postId && (
								<>
									{" "}
									on{" "}
									<a href={LinkGen.Post(conversation.postId)}>
										Post #{conversation.postId}
									</a>
								</>
							)}
						</span>
					</div>
				</div>
				<div className="inbox-view-actions">
					<button
						type="button"
						className="inbox-view-action-btn"
						onClick={handleArchiveToggle}
						title={conversation.isArchived ? "Unarchive" : "Archive"}
					>
						<Icon
							name={conversation.isArchived ? "unarchive" : "archive"}
							size={20}
						/>
					</button>
					<button
						type="button"
						className="inbox-view-action-btn"
						onClick={onClose}
						title={isTablet ? "Back to list" : "Close"}
						aria-label={
							isTablet ? "Back to conversation list" : "Close conversation"
						}
					>
						<Icon name={isTablet ? "arrow_back" : "close"} size={20} />
					</button>
				</div>
			</div>

			<div className="inbox-view-messages">
				{messages.length === 0 ? (
					<div className="inbox-view-empty">
						<p>No messages in this conversation yet.</p>
					</div>
				) : (
					<>
						{messages.map((message, index) => {
							const prevMessage = messages[index - 1];
							const showDate =
								!prevMessage ||
								format(new Date(message.sentAt), "yyyy-MM-dd") !==
									format(new Date(prevMessage.sentAt), "yyyy-MM-dd");

							return (
								<div key={message.id}>
									{showDate && (
										<div className="inbox-view-date">
											{format(new Date(message.sentAt), "MMMM d, yyyy")}
										</div>
									)}
									<InboxMessage
										message={message}
										onToggleHidden={
											canModerate && !message.isFromUser
												? (hidden) =>
														setCommentHidden.mutate({
															conversationId,
															messageId: message.id,
															hidden,
														})
												: undefined
										}
										isModerating={setCommentHidden.isPending}
										onSuggestReply={
											conversation.conversationType === "comment" &&
											!message.isFromUser
												? () => handleSuggestReply(message.id)
												: undefined
										}
										isSuggesting={suggestReply.isPending}
									/>
								</div>
							);
						})}
						<div ref={messagesEndRef} />
					</>
				)}
			</div>

			<InboxReplyComposer
				content={draft}
				setContent={setDraft}
				onSend={handleSendMessage}
				isSending={sendMessage.isPending}
				disabled={conversation.isArchived}
			/>
		</div>
	);
};
